/**
 * VoiceRecorder — DS Pattern (recorder canonique).
 *
 * Recorder réel de `/vocal` encapsulé en composant DS autonome : cercle radial
 * violet + halo + 9 barres sonores, 6 états, une couleur chacun (plan VREC1
 * §3.6), 2 tailles : lg 320 px (/vocal, défaut), sm 96 px (fenêtre
 * /vocal2). Consommé par `/vocal` ET la vitrine `/admin/design-system`.
 * Racine `<button>` (a11y clavier native). La logique « armé ou non » reste
 * côté consommateur, qui passe l'état via `state`.
 */

import styles from './VoiceRecorder.module.css';

export type VoiceRecorderState = 'off' | 'listening' | 'capturing' | 'processing' | 'confirming' | 'alert';
export type VoiceRecorderSize = 'lg' | 'sm';

interface VoiceRecorderProps {
  state?: VoiceRecorderState;
  size?: VoiceRecorderSize;
  onToggle?: () => void;
  label?: string;
  ariaLabel?: string;
  title?: string;
  className?: string;
}

const DEFAULT_ARIA: Record<VoiceRecorderState, string> = {
  off: 'Activer la saisie vocale',
  listening: 'À l\'écoute',
  capturing: 'Saisie en cours',
  processing: 'Traitement en cours',
  confirming: 'En attente de confirmation',
  alert: 'Problème de saisie vocale',
};

export function VoiceRecorder({
  state = 'off',
  size = 'lg',
  onToggle,
  label,
  ariaLabel,
  title,
  className = '',
}: VoiceRecorderProps) {
  return (
    <button
      type="button"
      className={`${styles.recorder} ${styles[state]} ${size === 'sm' ? styles.sm : ''} ${className}`}
      data-size={size}
      aria-busy={state === 'processing' || undefined}
      onClick={onToggle}
      aria-label={ariaLabel ?? DEFAULT_ARIA[state]}
      title={title}
    >
      <div className={styles.circleWrapper}>
        <div className={styles.outerCircle} />
        <div className={styles.bubble}>
          <div className={styles.wave}>
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className={styles.bar} />
            ))}
          </div>
        </div>
      </div>
      {label && <span className={styles.label}>{label}</span>}
    </button>
  );
}
